export type CalendarMode = 'date' | 'range' | 'month' | 'year' | 'datetime'

export type CalendarRangeValue = {
  start: Date | null
  end: Date | null
}

export const CALENDAR_MODES: CalendarMode[] = [
  'date',
  'range',
  'month',
  'year',
  'datetime',
]

export const CALENDAR_MODE_LABELS: Record<CalendarMode, string> = {
  date: 'Select date',
  range: 'From → To date',
  month: 'Select month',
  year: 'Select year',
  datetime: 'Date & time',
}

export const MONTH_SHORT_LABELS = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const

export const WEEKDAY_SHORT_LABELS = [
  'Sun',
  'Mon',
  'Tue',
  'Wed',
  'Thu',
  'Fri',
  'Sat',
] as const

/** 0 = Sunday, 1 = Monday, ... 6 = Saturday */
export type CalendarWeekday = 0 | 1 | 2 | 3 | 4 | 5 | 6

export const CALENDAR_WEEKDAYS: CalendarWeekday[] = [0, 1, 2, 3, 4, 5, 6]

export const isDateAllowedByWeekdays = (
  date: Date,
  allowedWeekdays?: CalendarWeekday[],
): boolean => {
  if (!allowedWeekdays?.length) {
    return true
  }

  return allowedWeekdays.includes(date.getDay() as CalendarWeekday)
}

export const isCalendarDateDisabled = (
  date: Date,
  options?: {
    allowedWeekdays?: CalendarWeekday[]
    isDateDisabled?: (date: Date) => boolean
  },
): boolean => {
  if (
    options?.allowedWeekdays?.length &&
    !isDateAllowedByWeekdays(date, options.allowedWeekdays)
  ) {
    return true
  }

  return options?.isDateDisabled?.(date) ?? false
}

export type CalendarDayCell = {
  date: Date
  day: number
  isCurrentMonth: boolean
  isSunday: boolean
  isSaturday: boolean
}

export const isSameDay = (left: Date, right: Date): boolean =>
  left.getFullYear() === right.getFullYear() &&
  left.getMonth() === right.getMonth() &&
  left.getDate() === right.getDate()

export const isBetweenDays = (date: Date, start: Date, end: Date): boolean => {
  const time = new Date(date).setHours(0, 0, 0, 0)
  const startTime = new Date(start).setHours(0, 0, 0, 0)
  const endTime = new Date(end).setHours(0, 0, 0, 0)
  const min = Math.min(startTime, endTime)
  const max = Math.max(startTime, endTime)
  return time > min && time < max
}

export const formatMonthYear = (date: Date): string =>
  `${MONTH_SHORT_LABELS[date.getMonth()]} ${date.getFullYear()}`

export const getYearPageStart = (year: number): number =>
  year - ((year - 2019 + 1200) % 12)

export const getCalendarDays = (
  viewDate: Date,
  weekStartsOn = 0,
): CalendarDayCell[] => {
  const year = viewDate.getFullYear()
  const month = viewDate.getMonth()
  const firstDay = new Date(year, month, 1)
  const startOffset = (firstDay.getDay() - weekStartsOn + 7) % 7
  const daysInMonth = new Date(year, month + 1, 0).getDate()
  const totalCells = Math.ceil((startOffset + daysInMonth) / 7) * 7
  const gridStart = new Date(year, month, 1 - startOffset)

  return Array.from({ length: totalCells }, (_, index) => {
    const date = new Date(
      gridStart.getFullYear(),
      gridStart.getMonth(),
      gridStart.getDate() + index,
    )

    return {
      date,
      day: date.getDate(),
      isCurrentMonth: date.getMonth() === month,
      isSunday: date.getDay() === 0,
      isSaturday: date.getDay() === 6,
    }
  })
}
